import React, { useState, useMemo, useRef, useEffect } from 'react';

import { Input } from 'antd';
import _ from 'lodash';

import Dinero from 'dinero.js';
import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';
import { getNow } from '@libs/NwMoment';

import { useUserPermission } from '@contexts/UserContext';

import { forceReloadPayments, usePayments } from '@hooks/API/UsePayments';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import EmptyState from '@components/Gui/EmptyState';
import { Loading } from '@components/Gui/Loading';
import Money from '@components/Gui/Money';
import NwIcon from '@components/Gui/NwIcon';
import PeriodSelector from '@components/Gui/PeriodSelector';
import StyleVariables from '@components/Gui/StyleVariables.js';
import PaymentDrawer from '@components/Payments/PaymentDrawer';

import { useStatementContext } from '../StatementContext';
import { StatementContainer } from '../StatementUI';
import PaymentItem from './PaymentItem';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/**
 * PaymentsWrapper
 *
 * Universal wrapper for payments tab.
 * Reusable across all contact types.
 */

const Wrapper = styled.div``;

const TotalsRow = styled.div`
    background: ${StyleVariables.PrimaryColor}11;
    display: flex;
    flex-direction: row;
    border-bottom: ${(props) => props.theme.DefaultBorder};
    border-top: 2px solid ${StyleVariables.PrimaryColor};

    .totals-label {
        width: 8rem;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        text-transform: uppercase;
        font-size: ${(props) => props.theme.FontSizeSm};
        border-right: ${(props) => props.theme.DefaultBorder};
    }

    .totals-detail {
        padding: 0.5rem;
        flex: 1 1 0;
        display: flex;
        align-items: center;
        border-right: ${StyleVariables.DarkerBorder};

        span {
            font-size: ${(props) => props.theme.FontSizeSm};
            opacity: 0.75;
        }
    }

    .totals-amounts {
        width: 10rem;
        padding: 0.5rem 0.5rem;
        text-align: right;

        .totals-currency {
            text-align: right;
            font-weight: bold;
        }
    }
`;

const searchInText = (text, searchTerm) => {
    if (text && text.toLowerCase().includes(searchTerm.toLowerCase())) {
        return true;
    }
    return false;
};

const getDinero = (amount, currency, defaultCurrency) =>
    Dinero({ amount: Math.round((amount ? amount : 0) * 100), currency: currency || defaultCurrency });

const PaymentsWrapper = ({ contactID, onUpdateTransactions }) => {
    const defaultPeriod = [getMoment('1900-01-01'), getNow().endOf('year')];
    const { contactType } = useStatementContext();
    const configuration = useConfigurations();

    const [searchTerm, setSearchTerm] = useState('');
    const [searchMode, setSearchMode] = useState(false);
    const [range, setRange] = useState(defaultPeriod);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const searchRef = useRef(null);
    const accessAccounting = useUserPermission('AccessAccounting');

    const { data: payments, isFetching } = usePayments(contactID);

    const handleChangeSearchTerm = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleClearSearchTerm = () => {
        setSearchMode(false);
        setSearchTerm('');
    };

    const handlePaymentClick = (paymentID) => {
        if (accessAccounting) {
            setSelectedPayment(paymentID);
        }
    };

    const handleClosePaymentDrawer = () => {
        forceReloadPayments(contactID);
        onUpdateTransactions();
        setSelectedPayment(null);
    };

    useEffect(() => {
        if (searchMode) {
            searchRef.current.focus();
        }
    }, [searchMode]);

    const filterPayments = (paymentstosearch, searchTerm) => {
        return paymentstosearch.filter((payment) => {
            if (
                searchInText(payment.Name, searchTerm) ||
                searchInText(payment.Number, searchTerm) ||
                searchInText(payment.HeaderNote, searchTerm) ||
                searchInText(payment.FooterNote, searchTerm) ||
                searchInText(payment.AddressNote, searchTerm)
            ) {
                return true;
            }
            return false;
        });
    };

    const activePayments = useMemo(() => {
        if (payments && payments.length > 0) {
            let act_payments = payments.filter((payment) => {
                return getMoment(payment.Date).isBetween(range[0], range[1], null, '[]');
            });
            if (searchTerm) {
                act_payments = filterPayments(act_payments, searchTerm);
            }
            return act_payments;
        }
        return [];
    }, [payments, range, searchTerm]);

    const paymentsTotals = useMemo(() => {
        const defaultCurrency = configuration.defaultCurrency;
        const totals = [];
        if (activePayments && activePayments.length > 0) {
            // Get all unique currencies from payments
            const paymentCurrencies = activePayments.map((p) => p.Currency);
            paymentCurrencies.push(defaultCurrency);
            const currencies = [...new Set(paymentCurrencies)];

            // Initialize totals for each currency
            for (const cur of currencies) {
                totals.push({
                    Currency: cur,
                    isDefaultCurrency: cur === defaultCurrency,
                    Dinero: getDinero(0, cur, defaultCurrency),
                    paymentCount: 0,
                });
            }

            // Sum TotalAmount for each payment
            for (const payment of activePayments) {
                const currencyItem = _.find(totals, { Currency: payment.Currency });
                if (currencyItem) {
                    currencyItem.Dinero = currencyItem.Dinero.add(
                        getDinero(payment.TotalAmount, payment.Currency, defaultCurrency)
                    );
                    currencyItem.paymentCount++;
                }
            }
        } else {
            totals.push({
                Currency: defaultCurrency,
                isDefaultCurrency: true,
                Dinero: getDinero(0, defaultCurrency, defaultCurrency),
                paymentCount: 0,
            });
        }
        return totals;
    }, [activePayments, configuration.defaultCurrency]);

    return (
        <StatementContainer>
            <div className="statement-header">
                <div className="statement-navigation">
                    <PeriodSelector period={range} onChange={setRange} alwaysEnabled defaultOption="always" />
                    <div className="statement-search">
                        <NwIcon icon={byPrefixAndName.fasl['search']} />
                        <Input
                            autoComplete="off"
                            value={searchTerm}
                            placeholder="Search payment (title, number or note)"
                            onChange={handleChangeSearchTerm}
                            allowClear={{
                                clearIcon: (
                                    <NwIcon
                                        icon={byPrefixAndName.fass['circle-xmark']}
                                        onClick={handleClearSearchTerm}
                                    />
                                ),
                            }}
                        />
                    </div>
                </div>
            </div>
            {isFetching ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem 0' }}>
                    <Loading text="" textBlack />
                </div>
            ) : activePayments && activePayments.length > 0 ? (
                <Wrapper>
                    {activePayments.map((payment) => (
                        <PaymentItem
                            key={payment.ID}
                            payment={payment}
                            onPaymentClick={handlePaymentClick}
                            hasAccountingAccess={accessAccounting}
                        />
                    ))}
                    <TotalsRow>
                        <div className="totals-label">Total</div>
                        <div className="totals-detail">
                            <span>
                                {activePayments.length} payment{activePayments.length !== 1 ? 's' : ''}
                            </span>
                        </div>
                        <div className="totals-amounts">
                            {paymentsTotals.map((total) => (
                                <div key={total.Currency} className="totals-currency">
                                    <Money amount={total.Dinero.toUnit()} currency={total.Currency} />
                                </div>
                            ))}
                        </div>
                    </TotalsRow>
                    {selectedPayment && (
                        <PaymentDrawer
                            paymentID={selectedPayment}
                            contactType={contactType}
                            onClose={handleClosePaymentDrawer}
                        />
                    )}
                </Wrapper>
            ) : (
                <Wrapper>
                    <EmptyState message="No payments" />
                </Wrapper>
            )}
        </StatementContainer>
    );
};

export default PaymentsWrapper;
